"use client";
import type { Route } from "next";
import { NavigateOptions } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { useRouter } from "next/navigation";
import { RedirectType } from "next/navigation";
import { useEffect } from "react";

/**
 * ClientRedirect Component
 *
 * This component exists as a workaround for an issue with RCS (Redirect Component System)
 * redirects throwing errors in certain scenarios. Instead of using Next.js's built-in
 * redirect() function which can cause server-side errors, this client-side component
 * handles navigation programmatically using the router.
 *
 * The component supports both replace and push navigation types and returns null
 * to avoid rendering any UI elements while performing the redirect.
 */

interface Props {
	href: Route;
	type?: RedirectType;
	options?: NavigateOptions;
}

export default function ClientRedirect({
	href,
	type = RedirectType.replace,
	options,
}: Props) {
	const router = useRouter();

	useEffect(() => {
		// Use router.replace() for replace navigation, router.push() for push navigation
		// This avoids the RCS redirect errors that can occur with server-side redirects
		type === RedirectType.replace
			? router.replace(href, options)
			: router.push(href, options);
	}, []);

	// Return null since this component is only used for navigation, not rendering
	return null;
}
